/**
 * Drip Scene (Merchandise)
 *
 * A dark carousel of relics. Each item emerges from the void,
 * flanked by ghosts barely visible at the edges.
 * Incantations decode from Elder Futhark runes into English.
 */

import type { Scene } from '@/types/scene';
import { fetchProducts, type Product } from '@/lib/supabase';

/* ── Data ────────────────────────────────────────────────────── */

export interface DripItem {
  id: string;
  name: string;
  incantation: string;
  image: string;
  href: string;
}

export const items: DripItem[] = [
  {
    id: 'drip-beanie-01',
    name: 'FANGS Beanie',
    incantation:
      'PLEASE NOTE: This item is a PREORDER. All orders containing this item are expected to ship the week of May 11th, 2026',
    image: '/images/drip/VampiresofTokyo_Beanie_Mockup.png',
    href: 'https://vampires-of-tokyo-merch.myshopify.com/products/vot-beanie?variant=51288330731744',
  },
  {
    id: 'drip-shirt-01',
    name: 'ARE YOU ONE OF US? Crewneck',
    incantation:
      'Screen print on black cotton. \n\n PLEASE NOTE: This item is a PREORDER. All orders containing this item are expected to ship the week of May 11th, 2026',
    image: '/images/drip/VampiresofTokyo_AreYouOneofUsSweatshirt_Shirt.png',
    href: 'https://vampires-of-tokyo-merch.myshopify.com/products/are-you-one-of-us-crewneck?variant=51329693057248',
  },
];

/**
 * Load drip items from Supabase, falling back to the hardcoded array.
 */
export async function loadDripItems(): Promise<Product[]> {
  const remote = await fetchProducts('drip');
  return remote ?? items;
}

/* ── Rune reveal ─────────────────────────────────────────────── */

const RUNES = 'ᚠᚢᚦᚨᚱᚲᚷᚹᚺᚾᛁᛃᛇᛈᛉᛊᛏᛒᛖᛗᛚᛝᛟᛞᚳᚻᛄᛋ';

/** Map each character: letters → rune, everything else (spaces, punctuation) → itself */
function runeFor(ch: string): string {
  if (/[a-zA-Z]/.test(ch)) return RUNES.charAt(Math.floor(Math.random() * RUNES.length));
  return ch;
}

/** Fisher-Yates shuffle (in place) */
function shuffle(arr: number[]): number[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = arr[i];
    if (tmp === undefined || arr[j] === undefined) continue;
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

/**
 * Three-phase rune reveal with zero layout shift:
 *
 *   Phase 1 — Rune text fades in. Each character lives in its own <span>.
 *             The Latin glyph is invisible but in-flow (drives the width),
 *             so line breaks are locked to the final English text.
 *   Phase 2 — Characters resolve in random order. Each resolve is a CSS
 *             crossfade: rune fades out, Latin fades in.
 *   Phase 3 — Clean-up: innerHTML replaced with plain text.
 */
function revealText(el: HTMLElement, text: string, s: DripState): void {
  // Cancel any previous animation
  for (const t of s.revealTimers) clearTimeout(t);
  s.revealTimers = [];

  // Split on double-newline for paragraph breaks
  const paragraphs = text.split('\n\n');

  const html = paragraphs
    .map((para) => {
      const chars = para.split('');
      const inner = chars
        .map((c) => {
          if (!/[a-zA-Z]/.test(c)) return c === ' ' ? ' ' : c;
          const rune = runeFor(c);
          return `<span class="rc"><span class="rc-latin">${c}</span><span class="rc-rune">${rune}</span></span>`;
        })
        .join('');
      return `<span class="rc-para">${inner}</span>`;
    })
    .join('');

  el.innerHTML = html;

  // Clear any preload inline style so animation can drive opacity
  el.style.removeProperty('opacity');

  // ── Phase 1: Fade in the rune text ──
  el.classList.add('rune-fadein');

  const FADEIN_MS = 600; // hold pure runes before decode
  const REVEAL_MS = 2400; // total decode window
  const CLEANUP_MS = 400; // extra time after last crossfade

  // Collect indices of letter spans and shuffle for random resolve
  const rcSpans = el.querySelectorAll<HTMLElement>('.rc');
  const order = shuffle([...new Array(rcSpans.length).keys()]);
  const gap = rcSpans.length > 1 ? REVEAL_MS / (rcSpans.length - 1) : 0;

  // ── Phase 2: After fade-in hold, begin random crossfades ──
  const phaseTimer = setTimeout(() => {
    el.classList.remove('rune-fadein');

    order.forEach((spanIdx, step) => {
      const delay = step * gap;
      const t = setTimeout(() => {
        rcSpans[spanIdx]?.classList.add('resolved');
      }, delay);
      s.revealTimers.push(t);
    });

    // ── Phase 3: Clean up DOM after all crossfades finish ──
    const cleanupTimer = setTimeout(() => {
      el.innerHTML = paragraphs.map((p) => `<span class="rc-para">${p}</span>`).join('');
    }, REVEAL_MS + CLEANUP_MS);
    s.revealTimers.push(cleanupTimer);
  }, FADEIN_MS);

  s.revealTimers.push(phaseTimer);
}

/* ── Carousel state & logic ──────────────────────────────────── */

interface DripState {
  items: DripItem[];
  currentIndex: number;
  revealTimers: ReturnType<typeof setTimeout>[];
  transitioning: boolean;
  keyHandler: (e: KeyboardEvent) => void;
  clickHandlers: { el: Element; handler: () => void }[];
}

let state: DripState | null = null;

function wrap(index: number): number {
  if (!state) return 0;
  const len = state.items.length;
  return ((index % len) + len) % len;
}

function updateDisplay(): void {
  if (!state) return;

  const idx = state.currentIndex;
  const prev = state.items[wrap(idx - 1)];
  const current = state.items[wrap(idx)];
  const next = state.items[wrap(idx + 1)];
  if (!prev || !current || !next) return;

  // Ghost images
  const prevImg = document.querySelector<HTMLImageElement>('.drip-ghost-prev img');
  const nextImg = document.querySelector<HTMLImageElement>('.drip-ghost-next img');
  if (prevImg) {
    prevImg.src = prev.image;
    prevImg.alt = prev.name;
  }
  if (nextImg) {
    nextImg.src = next.image;
    nextImg.alt = next.name;
  }

  // Active relic
  const activeImg = document.querySelector<HTMLImageElement>('.drip-relic-active img');
  const link = document.querySelector<HTMLAnchorElement>('.drip-relic-link');
  if (activeImg) {
    activeImg.src = current.image;
    activeImg.alt = current.name;
  }
  if (link) link.href = current.href;

  // Name + incantation
  const nameEl = document.querySelector<HTMLElement>('.drip-relic-name');
  const incantEl = document.querySelector<HTMLElement>('.drip-incantation-text');
  if (nameEl) nameEl.textContent = current.name;
  if (incantEl) revealText(incantEl, current.incantation, state);
}

function navigate(direction: number): void {
  if (!state || state.transitioning) return;
  state.transitioning = true;

  const relic = document.querySelector<HTMLElement>('.drip-relic-active');
  const incant = document.querySelector<HTMLElement>('.drip-incantation');

  // Fade out current
  const exitClass = direction > 0 ? 'exit-left' : 'exit-right';
  relic?.classList.add(exitClass);
  incant?.classList.add('fading');

  setTimeout(() => {
    if (!state) return;
    state.currentIndex = wrap(state.currentIndex + direction);
    updateDisplay();

    // Swap exit → enter
    relic?.classList.remove(exitClass);
    const enterClass = direction > 0 ? 'enter-right' : 'enter-left';
    relic?.classList.add(enterClass);
    incant?.classList.remove('fading');

    setTimeout(() => {
      relic?.classList.remove(enterClass);
      if (state) state.transitioning = false;
    }, 450);
  }, 280);
}

/* ── Lifecycle ───────────────────────────────────────────────── */

export function initDrip(overrideItems?: DripItem[]): void {
  const activeItems = overrideItems ?? items;
  const clickHandlers: DripState['clickHandlers'] = [];

  const addClick = (selector: string, handler: () => void): void => {
    const el = document.querySelector(selector);
    if (el) {
      el.addEventListener('click', handler);
      clickHandlers.push({ el, handler });
    }
  };

  const keyHandler = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowLeft') navigate(-1);
    else if (e.key === 'ArrowRight') navigate(1);
  };

  state = {
    items: activeItems,
    currentIndex: 0,
    revealTimers: [],
    transitioning: false,
    keyHandler,
    clickHandlers,
  };

  addClick('.drip-nav-prev', () => {
    navigate(-1);
  });
  addClick('.drip-nav-next', () => {
    navigate(1);
  });
  addClick('.drip-ghost-prev', () => {
    navigate(-1);
  });
  addClick('.drip-ghost-next', () => {
    navigate(1);
  });

  document.addEventListener('keydown', keyHandler);

  // Initial incantation decode after scene transition settles
  setTimeout(() => {
    updateDisplay();
  }, 150);
}

export function destroyDrip(): void {
  if (!state) return;
  for (const t of state.revealTimers) clearTimeout(t);
  document.removeEventListener('keydown', state.keyHandler);
  for (const { el, handler } of state.clickHandlers) {
    el.removeEventListener('click', handler);
  }
  state = null;
}

/* ── Scene definition ────────────────────────────────────────── */

export const drip: Scene = {
  id: 'drip',
  name: 'Drip',
  atmosphere: {
    background: 'linear-gradient(180deg, #0f0a0a 0%, #180f0f 100%)',
    fogIntensity: 0.3,
    noiseHue: 0,
    noiseBrightness: 0.6,
    noiseOpacity: 0.45,
    // noiseFilter: 'dense',
  },
  interactions: [
    {
      id: 'back-home',
      target: '[data-interaction="back-home"]',
      action: { type: 'navigate', sceneId: 'home' },
    },
    {
      id: 'nav-music',
      target: '[data-interaction="nav-music"]',
      action: { type: 'navigate', sceneId: 'music' },
    },
    {
      id: 'nav-drip',
      target: '[data-interaction="nav-drip"]',
      action: { type: 'navigate', sceneId: 'drip' },
    },
    {
      id: 'nav-more',
      target: '[data-interaction="nav-more"]',
      action: { type: 'navigate', sceneId: 'more' },
    },
  ],
  transitions: [
    { to: 'home', effect: 'fade', duration: 800 },
    { to: 'music', effect: 'fade', duration: 800 },
    { to: 'drip', effect: 'fade', duration: 800 },
    { to: 'more', effect: 'fade', duration: 800 },
  ],
  render: () => {
    const first = items.at(0);
    const last = items.at(-1);
    const second = items.at(1);
    if (!first || !last || !second) return '';
    return `
    <div class="pathway-content drip-scene">
      <div class="drip-showcase">
        <div class="drip-ghost drip-ghost-prev" role="button" tabindex="0" aria-label="Previous item">
          <img src="${last.image}" alt="${last.name}" />
        </div>
        <div class="drip-focus">
          <a class="drip-relic-link" href="${first.href}" target="_blank" rel="noopener noreferrer">
            <div class="drip-relic-active">
              <img src="${first.image}" alt="${first.name}" />
            </div>
          </a>
          <div class="drip-incantation">
            <span class="drip-relic-name">${first.name}</span>
            <p class="drip-incantation-text" style="opacity:0">${first.incantation
              .split('\n\n')
              .map((p) => `<span class="rc-para">${p}</span>`)
              .join('')}</p>
          </div>
        </div>
        <div class="drip-ghost drip-ghost-next" role="button" tabindex="0" aria-label="Next item">
          <img src="${second.image}" alt="${second.name}" />
        </div>
        <div class="drip-mask drip-mask-left"></div>
        <div class="drip-mask drip-mask-right"></div>
        <button class="drip-nav drip-nav-prev" aria-label="Previous">\u2039</button>
        <button class="drip-nav drip-nav-next" aria-label="Next">\u203A</button>
      </div>
    </div>
  `;
  },
};
